import type { DependencyList } from 'react';
import { CaretLeft, MagnifyingGlass, XCircle } from '@phosphor-icons/react';
import { usePageControls } from '../IslandProvider';
import { useViewport } from '../hooks';
import { istKompakt } from '../island-size';
import { rasterZeilen } from './layout';
import { Widget } from './Widget';
import type { PillenSteuerung } from './typen';

/** Suchfeld oben in der Steuerung (iOS-Stil: Lupe, Löschkreuz, ⌘K-Hinweis). */
function SteuerungsSuche({ platzhalter, wert, onChange, mobil }: { platzhalter: string; wert: string; onChange: (v: string) => void; mobil: boolean }) {
  return (
    <label className="pille-suche flex items-center" style={{ height: 44, borderRadius: 22, gap: 10, padding: '0 8px 0 14px' }}>
      <MagnifyingGlass size={19} weight="bold" color={wert ? '#f2d9a8' : 'rgba(235,235,245,0.6)'} className="shrink-0" aria-hidden />
      <input value={wert} onChange={e => onChange(e.target.value)} placeholder={platzhalter} aria-label={platzhalter}
        className="min-w-0 flex-1" style={{ border: 'none', outline: 'none', background: 'transparent', color: '#fff', fontSize: mobil ? 16 : 17, fontWeight: 500, caretColor: '#f2d9a8', fontFamily: 'inherit' }} />
      {wert ? (
        <button type="button" onClick={() => onChange('')} aria-label="Suche leeren" className="pille-pop flex shrink-0 cursor-pointer items-center justify-center"
          style={{ width: mobil ? 44 : 28, height: mobil ? 44 : 28, border: 'none', background: 'transparent', color: 'rgba(235,235,245,0.6)' }}>
          <XCircle size={20} weight="fill" />
        </button>
      ) : !mobil && (
        <kbd className="shrink-0" style={{ height: 22, padding: '0 6px', borderRadius: 6, fontSize: 10, fontWeight: 800, lineHeight: '22px', background: 'rgba(255,255,255,0.12)', color: 'rgba(235,235,245,0.6)', fontFamily: 'inherit' }}>⌘K</kbd>
      )}
    </label>
  );
}

/** Steuerung eines Bereichs als iOS-Widgets (Handoff Pille §6). */
export function SteuerungsAnsicht({ steuerung }: { steuerung: PillenSteuerung }) {
  const vp = useViewport();
  const mobil = istKompakt(vp.width, vp.height);
  const zeilen = rasterZeilen(steuerung.eintraege);
  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      {steuerung.zurueck && (
        <button type="button" onClick={steuerung.zurueck.onClick} className="flex cursor-pointer items-center self-start"
          style={{ height: mobil ? 44 : 36, gap: 6, padding: '0 14px 0 10px', borderRadius: 18, border: 'none', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' }}>
          <CaretLeft size={14} weight="bold" />{steuerung.zurueck.label}
        </button>
      )}
      {steuerung.suche && <SteuerungsSuche {...steuerung.suche} mobil={mobil} />}
      {zeilen.map((z, i) => z.art === 'abschnitt' ? (
        <span key={`a${i}`} className="uppercase" style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', color: 'rgba(235,235,245,0.5)', padding: '6px 4px 0' }}>{z.titel}</span>
      ) : (
        <div key={z.widgets.map(w => w.id).join('+')} className="grid" style={{ gridTemplateColumns: z.widgets.length === 2 ? 'repeat(2, minmax(0,1fr))' : 'minmax(0,1fr)', gap: 10 }}>
          {z.widgets.map(w => <Widget key={w.id} w={w} mobil={mobil} />)}
        </div>
      ))}
    </div>
  );
}

/**
 * Registriert die Steuerung eines Bereichs in der Pille – als Daten, gezeichnet von der generischen
 * SteuerungsAnsicht. `deps` wie bei usePageControls: alle Werte, die Anzeige oder Callbacks beeinflussen.
 */
export function usePillenSteuerung(steuerung: PillenSteuerung, deps: DependencyList, enabled = true): void {
  usePageControls(<SteuerungsAnsicht steuerung={steuerung} />, steuerung.titel, [steuerung.titel, ...deps], enabled);
}
